/**
 * CS-101-a5 (Research Paper Draft) use-case map.
 *
 * Q1 and Q2 are fixed demos (quality anchor + 3x variation anchor). They stay
 * documented here but are outside scenario walkthrough scope.
 *
 * Scenario scope starts at Q3. Every scenario row maps to one paper item.
 * Quality checks come from `snapshotFromPaperRow` + `runQuestionQualityChecks`.
 * Variation doors come from clean snapshots or `recommendVariation` when usage ≥ 3.
 */

export type UseCaseCategory =
  | "showcase"
  | "quality"
  | "variation"
  | "classification"
  | "performance"
  | "leftover"

export type UseCaseScope = "fixed-demo" | "scenario"

export type PaperUseCase = {
  id: string
  number: number
  scope: UseCaseScope
  category: UseCaseCategory
  /** Spreadsheet / demo script label */
  label: string
  /** Short stem anchor for copy-paste lists */
  stemAnchor: string
  /** What to open in Ask Leo or the inspector */
  leoEntry: string
  /** Primary check ids or job ids when relevant */
  signals?: string[]
  /** Full documentation for QA and demo scripts */
  doc: string
}

/** One performance pager header pattern on CS-101-a5. */
export type PerformanceHeaderUseCase = {
  signal: string
  checkId: string
  questionId: string
  number: number
  /** Full pager header with the stat inline (matches `performanceIssueHeaderCopy`). */
  header: string
  /** Stat only, shown as the inline pill in the pager header row. */
  highlight: string
  /** Action chips below the header (not repeated in the header). */
  chips: string[]
}

/** Scenario walkthroughs start here. Q1 and Q2 are fixed demos only. */
export const CS101_A5_SCENARIO_FIRST_NUMBER = 3

/** Every row on CS-101-a5, in Q# order. */
export const CS101_A5_USE_CASES: PaperUseCase[] = [
  {
    id: "q1",
    number: 1,
    scope: "fixed-demo",
    category: "showcase",
    label: "Fixed demo · Quality · 1 stem + 2 distractors",
    stemAnchor: "Beta oxidation of fatty acids occurs in",
    leoEntry:
      "Leo spotted: 1 incomplete stem · Ask Leo: stem rewrite with context distractors, then two unstated option fixes",
    signals: [
      "compliance-incomplete-stem",
      "clarity-fragment-stem",
      "distractor-unstated-opt-q1-B",
      "distractor-unstated-opt-q1-C",
    ],
    doc:
      "Quality anchor. Incomplete stem on the canvas. Two positional distractors refer to facts the stem never states. Inspector shows one compact Leo spotted card (1 incomplete stem, 2 issues in distractors). Ask Leo pager walks three line items: stem, option B, option C. Stem card shows rewritten question plus three context distractors and Use this stem.",
  },
  {
    id: "q2",
    number: 2,
    scope: "fixed-demo",
    category: "variation",
    label: "Fixed demo · Variation · 3x reuse in course",
    stemAnchor: "amino acid increases net negative charge",
    leoEntry:
      "Pager header: Used 3× in course. Returning students may recognize the wording. · Chips: See a different variation, Replace with another question",
    signals: [
      "performance-usage",
      "alternate-form",
      "variation-set",
    ],
    doc:
      "Variation anchor plus reuse performance header. Item is clean on quality. History shows 3 uses in the same course. Ask Leo pager lead: Used 3× in course (pill). Returning students may recognize the wording. Action chips only below: See a different variation, Replace with another question.",
  },
  {
    id: "q3",
    number: 3,
    scope: "scenario",
    category: "quality",
    label: "Quality · Stem and multiple distractors",
    stemAnchor: "Citric acid cycle takes place in",
    leoEntry: "Incomplete stem + All/None pair + too few functional distractors",
    signals: [
      "compliance-incomplete-stem",
      "distractor-aota-nota",
      "distractor-too-few",
      "compliance-no-rationale",
    ],
    doc:
      "Combined stem and distractor quality on one item. Fragment stem, All of the above and None of the above together, keyed None, unstated positional options, and missing rationale. Leo jobs: stem, distractors, rationale.",
  },
  {
    id: "q4",
    number: 4,
    scope: "scenario",
    category: "quality",
    label: "Quality · Only one unstated distractor",
    stemAnchor: "beta oxidation of very long chain fatty acids",
    leoEntry: "One option refers to something the stem never states",
    signals: ["distractor-unstated-d"],
    doc:
      "Single-option quality. Stem is complete. One distractor uses a positional phrase the stem never names. One line item in Ask Leo for that option.",
  },
  {
    id: "q5",
    number: 5,
    scope: "scenario",
    category: "quality",
    label: "Quality · Missing passage attachment",
    stemAnchor: "Read the following passage … heartbeat",
    leoEntry: "Add the missing passage before the stem",
    signals: ["compliance-missing-reference"],
    doc:
      "Reference compliance. Stem promises a passage that is not attached. Ask Leo offers a suggested pacemaker passage with Use this passage.",
  },
  {
    id: "q6",
    number: 6,
    scope: "scenario",
    category: "quality",
    label: "Quality · Change format (select-all stem on single choice)",
    stemAnchor: "Select all that apply for receptors",
    leoEntry: "Change format · select-all on an MCQ shell",
    signals: ["format-select-all"],
    doc:
      "Format misfit. Select-all stem on a single-select shell. Change format opportunity opens Leo for Multi-select.",
  },
  {
    id: "q7",
    number: 7,
    scope: "scenario",
    category: "quality",
    label: "Quality · Stem grammar cue (a/an)",
    stemAnchor: "principal intracellular buffer in erythrocytes is an",
    leoEntry: "Rewrite the stem as a complete question · fix a/an grammar cue",
    signals: ["clarity-fragment-stem", "cueing-grammar-key"],
    doc:
      "Stem fragment plus article cueing. Stem ends with an but only the key fits grammatically. Stem rewrite and vowel-start distractor fixes on one item.",
  },
  {
    id: "q8",
    number: 8,
    scope: "scenario",
    category: "quality",
    label: "Quality · Option length cues the key",
    stemAnchor: "longest option cues the keyed amino acid",
    leoEntry: "Balance key length with the distractors",
    signals: ["cueing-longest-key"],
    doc:
      "Length cueing. Key is much longer than every distractor. Use this option shortens the keyed answer.",
  },
  {
    id: "q9",
    number: 9,
    scope: "scenario",
    category: "quality",
    label: "Quality · Mark NOT or EXCEPT",
    stemAnchor: "Which enzyme is not involved",
    leoEntry: "Mark NOT or EXCEPT in the stem",
    signals: ["clarity-unmarked-negative"],
    doc:
      "Negative stem without NOT or EXCEPT markup. Apply marks the negative wording for students.",
  },
  {
    id: "q10",
    number: 10,
    scope: "scenario",
    category: "variation",
    label: "Variation · Stem only (fill in the blank)",
    stemAnchor: "facial expression … Fill in the blank",
    leoEntry: "Give me another wording · stem variation on blank item",
    signals: ["variation-stem"],
    doc:
      "Clean FIB item. No quality defects. Variation-both job offers another stem wording only.",
  },
  {
    id: "q11",
    number: 11,
    scope: "scenario",
    category: "quality",
    label: "Quality · Matching format misfit",
    stemAnchor: "Match the disease states",
    leoEntry: "Change format · matching on MCQ shell",
    signals: ["format-misfit"],
    doc:
      "Format misfit. Matching stem on MCQ shell. Change format to Match the following.",
  },
  {
    id: "q12",
    number: 12,
    scope: "scenario",
    category: "variation",
    label: "Performance · Weak disc. · Variation · Stem and some distractors",
    stemAnchor: "chamber of the heart receives deoxygenated blood",
    leoEntry:
      "Pager header: Disc. 0.32 is weak on this item. · Chips: See a different variation, Swap distractors · plus stem/distractor variation cards",
    signals: [
      "performance-discrimination",
      "variation-both",
      "variation-option-one",
      "variation-option-few",
    ],
    doc:
      "Weak discrimination performance header on a clean MCQ. PBI 0.22 is above the review floor but below adequate; Disc. 0.32 drives the pager lead. Action chips: See a different variation, Swap distractors. Variation jobs still offer stem wordings, one distractor swap, and a pair of distractor swaps.",
  },
  {
    id: "q13",
    number: 13,
    scope: "scenario",
    category: "performance",
    label: "Performance · Low PBI · Quality · Title align",
    stemAnchor: "All of the following are true for enzymes, EXCEPT",
    leoEntry:
      "Pager header: PBI 0.12 is below the review floor. · Chips: Enhance with Leo, Replace with another question · plus align title",
    signals: [
      "performance-pbi",
      "clarity-title-mismatch",
      "belowReviewFloor",
    ],
    doc:
      "Low PBI performance header plus title mismatch quality. PBI 0.12 is below the review floor. Ask Leo pager lead: Point-biserial PBI 0.12 (pill) is below the review floor. Action chips: Enhance with Leo, Replace with another question. Title does not match stem on a separate quality line item.",
  },
  {
    id: "q14",
    number: 14,
    scope: "scenario",
    category: "variation",
    label: "Variation · Stem and multiple distractors",
    stemAnchor: "primary site of ATP synthesis",
    leoEntry: "Give me another wording · Use all distractors",
    signals: ["variation-both", "variation-option-all"],
    doc:
      "Clean MCQ. Variation offers stem wordings and a full distractor set rewrite.",
  },
  {
    id: "q15",
    number: 15,
    scope: "scenario",
    category: "quality",
    label: "Quality · True/false format + history floor",
    stemAnchor: "Which of the following statements about Warfarin",
    leoEntry: "Change format · T/F misfit · history floor",
    signals: ["format-misfit", "belowReviewFloor"],
    doc:
      "T/F shell on MCQ stem plus weak history. Format change and floor subtitle on tile.",
  },
  {
    id: "q16",
    number: 16,
    scope: "scenario",
    category: "quality",
    label: "Quality · Align title with long essay stem",
    stemAnchor: "heart failure with preserved ejection fraction",
    leoEntry: "Align the title with the stem · essay format",
    signals: ["clarity-title-mismatch"],
    doc:
      "Essay item with title shorter than the stem topic. Align title with stem suggestion.",
  },
  {
    id: "q17",
    number: 17,
    scope: "scenario",
    category: "classification",
    label: "Classification · Bloom and difficulty unset",
    stemAnchor: "Describe what is required for this process",
    leoEntry: "Set Bloom's · Set difficulty · searchable title",
    signals: ["classification-bloom", "classification-difficulty"],
    doc:
      "Empty Bloom and difficulty on an essay. Classification job plus Give the item a searchable title.",
  },
  {
    id: "q18",
    number: 18,
    scope: "scenario",
    category: "variation",
    label: "Variation · Change reference passage",
    stemAnchor: "inward current of the cardiac pacemaker",
    leoEntry: "Swap passage · stem variation with attached reference",
    signals: ["variation-references"],
    doc:
      "MCQ with attached pacemaker passage. Variation can swap the reference HTML and offer stem wordings.",
  },
  {
    id: "q19",
    number: 19,
    scope: "scenario",
    category: "performance",
    label: "Performance · Difficulty drift",
    stemAnchor: "ketone bodies on blood pH",
    leoEntry:
      "Pager header: 28% correct, not the tagged difficulty. · Chips: Set difficulty to hard, See a different variation",
    signals: ["performance-difficulty", "usedCount:2"],
    doc:
      "Difficulty drift performance header. Tagged Easy but examinees scored 28% correct (hard band). Ask Leo pager lead: Examinees scored 28% correct (pill), not the tagged difficulty. Action chips: Set difficulty to hard, See a different variation. Used twice but below the 3× alternate-form threshold.",
  },
  {
    id: "q20",
    number: 20,
    scope: "scenario",
    category: "variation",
    label: "Variation · User does not know what to change",
    stemAnchor: "macrophages shape antigen presentation",
    leoEntry: "Give me a variation · open-ended essay variation ask",
    signals: ["variation-both", "variation-set"],
    doc:
      "Clean essay. Open variation ask when the author wants a different version without naming a field.",
  },
  {
    id: "q17-draft",
    number: 21,
    scope: "scenario",
    category: "quality",
    label: "Quality · Draft row with empty title",
    stemAnchor: "Describe.",
    leoEntry: "Give the item a searchable title · draft status",
    signals: ["clarity-short-title", "status:Draft"],
    doc:
      "Draft row with minimal stem and no title. Short title finding plus draft status in the paper.",
  },
]

export const CS101_A5_USE_CASE_BY_ID = Object.fromEntries(
  CS101_A5_USE_CASES.map((row) => [row.id, row]),
) as Record<string, PaperUseCase>

export const CS101_A5_FIXED_DEMO_USE_CASES = CS101_A5_USE_CASES.filter(
  (row) => row.scope === "fixed-demo",
)

export const CS101_A5_SCENARIO_USE_CASES = CS101_A5_USE_CASES.filter(
  (row) => row.scope === "scenario",
)

export function useCaseForQuestion(id: string): PaperUseCase | undefined {
  return CS101_A5_USE_CASE_BY_ID[id]
}

/**
 * Performance pager header demos on CS-101-a5 (one question per signal).
 * Header copy matches `performanceIssueHeaderCopy`; chips are action labels only.
 */
export const CS101_A5_PERFORMANCE_HEADER_USE_CASES: PerformanceHeaderUseCase[] =
  [
    {
      signal: "Reuse",
      checkId: "performance-usage",
      questionId: "q2",
      number: 2,
      header:
        "Used 3× in course. Returning students may recognize the wording.",
      highlight: "3× in course",
      chips: ["See a different variation", "Replace with another question"],
    },
    {
      signal: "Low PBI",
      checkId: "performance-pbi",
      questionId: "q13",
      number: 13,
      header: "Point-biserial PBI 0.12 is below the review floor.",
      highlight: "PBI 0.12",
      chips: ["Enhance with Leo", "Replace with another question"],
    },
    {
      signal: "Difficulty drift",
      checkId: "performance-difficulty",
      questionId: "q19",
      number: 19,
      header: "Examinees scored 28% correct, not the tagged difficulty.",
      highlight: "28% correct",
      chips: ["Set difficulty to hard", "See a different variation"],
    },
    {
      signal: "Weak disc.",
      checkId: "performance-discrimination",
      questionId: "q12",
      number: 12,
      header: "Discrimination Disc. 0.32 is weak on this item.",
      highlight: "Disc. 0.32",
      chips: ["See a different variation", "Swap distractors"],
    },
  ]

export const CS101_A5_PERFORMANCE_HEADER_BY_QUESTION_ID =
  Object.fromEntries(
    CS101_A5_PERFORMANCE_HEADER_USE_CASES.map((row) => [
      row.questionId,
      row,
    ]),
  ) as Record<string, PerformanceHeaderUseCase>

/** Tab-separated matrix for performance header demos. */
export const CS101_A5_PERFORMANCE_HEADER_COPY_LIST =
  CS101_A5_PERFORMANCE_HEADER_USE_CASES.map(
    (row) =>
      `Q${row.number}\t${row.questionId}\t${row.signal}\t${row.checkId}\t${row.header}\tpill: ${row.highlight}\tchips: ${row.chips.join(", ")}`,
  ).join("\n")

/** Copy-paste matrix for all rows including fixed demos. */
export const CS101_A5_USE_CASE_COPY_LIST = CS101_A5_USE_CASES.map(
  (row) =>
    `Q${row.number}\t${row.id}\t${row.scope}\t${row.category}\t${row.label}\t${row.stemAnchor}\t→ ${row.leoEntry}`,
).join("\n")

/** Scenario walkthrough matrix (Q3 onward). */
export const CS101_A5_SCENARIO_COPY_LIST = CS101_A5_SCENARIO_USE_CASES.map(
  (row) =>
    `Q${row.number}\t${row.id}\t${row.category}\t${row.label}\t${row.stemAnchor}\t→ ${row.leoEntry}`,
).join("\n")

/** One block per row for QA scripts. */
export const CS101_A5_USE_CASE_DOCUMENTATION = CS101_A5_USE_CASES.map(
  (row) =>
    [
      `Q${row.number} (${row.id}) · ${row.scope} · ${row.category}`,
      `Label: ${row.label}`,
      `Stem: ${row.stemAnchor}`,
      `Leo: ${row.leoEntry}`,
      row.signals?.length ? `Signals: ${row.signals.join(", ")}` : null,
      row.doc,
    ]
      .filter(Boolean)
      .join("\n"),
).join("\n\n")
